import { Inject, Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HostConfigService } from '@/common/host-config/host-config.service';
import { EnvironmentService } from '@/services/environment/environment.service';

@Injectable({
	providedIn: 'root',
})
export class SEOService {
	private readonly hostConfig = inject(HostConfigService);
	private readonly environmentService = inject(EnvironmentService);

	constructor(@Inject(DOCUMENT) private readonly document: Document) {}

	getBaseUrl(): string {
		if (this.hostConfig.isDefaultHost()) {
			return this.environmentService.environment?.production
				? 'https://oskar.com.pl'
				: 'https://dev.oskar.com.pl';
		}
		return this.hostConfig.baseUrl();
	}

	prepareMetaTagsPayload(
		description: string,
		title: string,
		canonicalLink: string,
		image: string,
	): Record<string, string>[] {
		const domain = this.getBaseUrl()
			.replace(/^https?:\/\//, '')
			.replace(/\/$/, '');
		return [
			{
				name: 'description',
				content: description,
			},
			{
				property: 'og:title',
				content: title,
			},
			{
				property: 'og:description',
				content: description,
			},
			{
				property: 'og:url',
				content: canonicalLink,
			},
			{
				property: 'og:image',
				content: image,
			},
			{
				property: 'og:type',
				content: 'website',
			},
			{
				name: 'twitter:description',
				content: description,
			},
			{
				name: 'twitter:title',
				content: title,
			},
			{
				name: 'twitter:image',
				content: image,
			},
			{
				property: 'twitter:url',
				content: canonicalLink,
			},
			{
				name: 'twitter:card',
				content: 'summary_large_image',
			},
			{
				property: 'twitter:domain',
				content: domain,
			},
		];
	}

	getCanonicalLink(href: string): string {
		const base = this.getBaseUrl();
		const path = href.startsWith('/') ? href : `/${href}`;
		return `${base.replace(/\/$/, '')}${path}`;
	}

	setCanonicalLink(href: string, options?: { withOrigin: boolean }): void {
		const canonical = options && options.withOrigin ? href : this.getCanonicalLink(href);
		const canonicalElement: HTMLLinkElement = this.document.head.querySelector("link[rel='canonical']");

		if (canonicalElement) {
			canonicalElement.href = canonical;
			return;
		}

		const link = this.document.createElement('link');

		link.setAttribute('href', canonical);
		link.setAttribute('rel', 'canonical');

		this.document.head.appendChild(link);
	}

	setPreconnectLink(url: string): void {
		const link = this.document.createElement('link');

		link.setAttribute('href', url);
		link.setAttribute('rel', 'preconnect');

		this.document.head.appendChild(link);
	}

	removePreconnectLinks(): void {
		const links = this.document.querySelectorAll("link[rel='preconnect']");
		links.forEach((link: Element) => link.parentNode?.removeChild(link));
	}
}
